import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import {
  fetchCandidateFeedback,
  FeedbackRefusedError,
  type CandidateFeedback,
  type FeedbackRound,
} from '../../services/taCandidateFeedbackApi';
import { COLORS } from './format';

/**
 * The candidate panel's feedback section: what every interview round said, newest first.
 *
 * The SAME shaping the 📋 chip's verdict comes from, so the chip and the words underneath it can
 * never disagree. Fetched when the panel opens and never before — the board's first load must not
 * pay for context nobody asked to see.
 *
 * The latest round is open on arrival because it is the question people come with; older rounds
 * are the context under it.
 *
 * ⚠️ Interviewer prose and Lever scores. It is TEXT, never markup, and this is a display surface:
 * nothing here may be piped into a candidate-facing draft (see
 * proxy-server/lib/candidateFeedback.js for the boundary).
 */

const fmtWhen = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/** The verdict as Lever stores it, coloured by the hire line. */
function RatingPill({ rating }: { rating: string }) {
  const bad = /\b[12]\b|no\s*hire/i.test(rating);
  const style = bad
    ? { backgroundColor: COLORS.redBg, color: COLORS.redText }
    : { backgroundColor: COLORS.greenBg, color: COLORS.greenText };
  return (
    <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={style}>
      {rating}
    </span>
  );
}

/**
 * One interviewer's scorecard, as a disclosure row.
 *
 * A plain <button> for the header, like the email history's message rows: it is a full-width row
 * (round, interviewer, date, verdict), not a labelled action.
 */
function RoundRow({ round, defaultOpen }: { round: FeedbackRound; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-col items-start gap-1 text-left"
      >
        <span className="flex w-full flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="font-semibold text-gray-800">{round.form}</span>
          {round.interviewer && <span>{round.interviewer}</span>}
          {round.completedAt && <span>{fmtWhen(round.completedAt)}</span>}
          {round.rating && <RatingPill rating={round.rating} />}
          <span className="ml-auto text-gray-400">{open ? '▾' : '▸'}</span>
        </span>
        {!open && round.answers.length > 0 && (
          <span className="line-clamp-1 text-sm text-gray-700">{round.answers[0].value}</span>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {round.answers.length === 0 && (
            <p className="text-sm text-gray-500">
              Nothing written — only the rating was filled in.
            </p>
          )}
          {round.answers.map((a, i) => (
            <div key={`${round.id}-${i}`}>
              <div className="text-xs font-semibold text-gray-500">{a.label}</div>
              {/* Interviewer prose is untrusted text: rendered as text, never as markup. */}
              <div className="whitespace-pre-wrap text-sm text-gray-900">{a.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The feedback section: every submitted round, newest first. */
function FeedbackSection({ data }: { data: CandidateFeedback }) {
  if (data.rounds.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No interview feedback has been submitted for this candidate yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {data.rounds.map((r, i) => (
        // Every form of the LATEST round opens on arrival — on a case or final round that is two
        // interviewers, and opening only the first would hide the disagreement the chip announced.
        <RoundRow key={r.id} round={r} defaultOpen={r.form === data.latestRound && i < 3} />
      ))}
    </div>
  );
}

export function PanelFeedback({ boardEmail, opportunityId }: { boardEmail: string; opportunityId: string }) {
  const { data, error, isPending, refetch } = useQuery<CandidateFeedback, Error>({
    queryKey: ['candidate-feedback', boardEmail, opportunityId],
    queryFn: () => fetchCandidateFeedback({ opportunityId, boardEmail }),
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof FeedbackRefusedError) && count < 1,
  });

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-900">📋 Interview feedback</h3>

      {isPending && (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner className="h-3 w-3" /> Loading the feedback…
        </p>
      )}

      {error instanceof FeedbackRefusedError && <p className="text-sm text-gray-600">{error.message}.</p>}
      {error && !(error instanceof FeedbackRefusedError) && (
        <div className="space-y-2">
          <p className="text-sm text-red-600">Could not load the feedback just now.</p>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && <FeedbackSection data={data} />}
    </section>
  );
}
